import React from 'react';

// Neon-Pulse Animation für den Zurück-Button
const neonStyle = document.getElementById('selenny-neon-style') || (() => {
  const s = document.createElement('style');
  s.id = 'selenny-neon-style';
  s.textContent = `
    @keyframes selenny-neon-pulse {
      0%, 100% { box-shadow: 0 0 8px rgba(0,255,136,0.6), 0 0 20px rgba(0,204,255,0.3); transform: scale(1); }
      50% { box-shadow: 0 0 16px rgba(0,255,136,0.9), 0 0 36px rgba(0,204,255,0.5); transform: scale(1.03); }
    }
  `;
  document.head.appendChild(s);
  return s;
})();

function TopToolbar({
  canvasManager,
  historyManager,
  gridManager,
  activeObject,
  canUndo,
  canRedo,
  gridVisible,
  previewMode,
  onTogglePreview,
  showRulers,
  onToggleRulers,
  isEmbedded,
  darkMode,
  onToggleDark,
  speechEnabled,
  onToggleSpeech,
  onShare,
  isSharedView,
  isAdmin,
  designId,
  onCopyAsTemplate,
}) {
  if (!canvasManager) return null;

  const btn = (onClick, tooltip, icon, disabled = false, active = false, guideId = null) => (
    <button
      onClick={onClick}
      data-tooltip={tooltip}
      data-tooltip-pos="bottom"
      disabled={disabled}
      className={`selenny-toolbar-btn ${active ? 'active' : ''} ${disabled ? 'opacity-30 cursor-not-allowed' : ''}`}
      {...(guideId ? { 'data-guide-id': guideId } : {})}
    >
      {icon}
    </button>
  );

  const hasSelection = !!activeObject;

  return (
    <div className="h-10 bg-white border-b border-gray-200 flex items-center px-2 gap-0.5 shrink-0 overflow-visible">
      {/* Undo / Redo */}
      {btn(() => historyManager?.undo(), 'Rückgängig (Ctrl+Z)',
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 10h10a5 5 0 0 1 0 10H9"/><path d="M3 10l4-4M3 10l4 4"/></svg>,
        !canUndo, false, 'btn-undo'
      )}
      {btn(() => historyManager?.redo(), 'Wiederholen (Ctrl+Y)',
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 10H11a5 5 0 0 0 0 10h4"/><path d="M21 10l-4-4M21 10l-4 4"/></svg>,
        !canRedo, false, 'btn-redo'
      )}

      <div className="selenny-divider" />

      {/* Selection */}
      {btn(() => canvasManager.deselectAll(), 'Auswahl aufheben (Esc)',
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 3h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z" strokeDasharray="4 2"/></svg>,
        !hasSelection
      )}
      {btn(() => canvasManager.deleteSelected(), 'Löschen (Entf)',
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"/></svg>,
        !hasSelection, false, 'btn-delete'
      )}
      {btn(() => { if (window.confirm('Bühne wirklich leeren? Alle Objekte werden gelöscht.')) canvasManager.clearCanvas(); }, 'Bühne leeren',
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"/><line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/></svg>
      )}

      <div className="selenny-divider" />

      {/* Alignment */}
      {btn(() => canvasManager.alignSelected('left'), 'Links ausrichten',
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 2v20M8 6h12M8 12h8M8 18h10"/></svg>,
        !hasSelection, false, 'btn-align-left'
      )}
      {btn(() => canvasManager.alignSelected('center-h'), 'Horizontal zentrieren',
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2v20M6 6h12M8 12h8M7 18h10"/></svg>,
        !hasSelection
      )}
      {btn(() => canvasManager.alignSelected('right'), 'Rechts ausrichten',
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 2v20M4 6h12M8 12h8M6 18h10"/></svg>,
        !hasSelection
      )}
      {btn(() => canvasManager.alignSelected('top'), 'Oben ausrichten',
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M2 4h20M6 8v12M12 8v8M18 8v10"/></svg>,
        !hasSelection
      )}
      {btn(() => canvasManager.alignSelected('center-v'), 'Vertikal zentrieren',
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M2 12h20M6 6v12M12 8v8M18 7v10"/></svg>,
        !hasSelection
      )}
      {btn(() => canvasManager.alignSelected('bottom'), 'Unten ausrichten',
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M2 20h20M6 4v12M12 8v8M18 6v10"/></svg>,
        !hasSelection
      )}

      <div className="selenny-divider" />

      {/* Flip / Rotate */}
      {btn(() => canvasManager.flipHorizontal(), 'Horizontal spiegeln',
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2v20M16 6l4 6-4 6M8 6L4 12l4 6"/></svg>,
        !hasSelection, false, 'btn-flip-h'
      )}
      {btn(() => canvasManager.flipVertical(), 'Vertikal spiegeln',
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M2 12h20M6 8l6-4 6 4M6 16l6 4 6-4"/></svg>,
        !hasSelection
      )}
      {btn(() => canvasManager.rotate(90), 'Um 90° drehen',
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 12a9 9 0 1 1-9-9"/><path d="M21 3v6h-6"/></svg>,
        !hasSelection, false, 'btn-rotate'
      )}

      <div className="selenny-divider" />

      {/* Duplicate */}
      {btn(() => canvasManager.duplicateSelected(), 'Duplizieren (Ctrl+D)',
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="8" y="8" width="12" height="12" rx="1"/><path d="M4 16V5a1 1 0 0 1 1-1h11"/></svg>,
        !hasSelection, false, 'btn-duplicate'
      )}

      {/* Layer order */}
      {btn(() => canvasManager.bringForward(), 'Ebene nach vorne',
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5l7 7H5z"/><path d="M12 13v6"/></svg>,
        !hasSelection
      )}
      {btn(() => canvasManager.sendBackward(), 'Ebene nach hinten',
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 19l7-7H5z"/><path d="M12 11V5"/></svg>,
        !hasSelection
      )}

      <div className="selenny-divider" />

      {/* Lock */}
      {btn(
        () => activeObject?.isLocked ? canvasManager.unlockSelected() : canvasManager.lockSelected(),
        activeObject?.isLocked ? 'Objekt entsperren' : 'Objekt sperren',
        activeObject?.isLocked
          ? <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="5" y="11" width="14" height="10" rx="2"/><path d="M8 11V7a4 4 0 0 1 8 0"/></svg>
          : <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="5" y="11" width="14" height="10" rx="2"/><path d="M8 11V7a4 4 0 0 1 8 0v4"/></svg>,
        !hasSelection, false, 'btn-lock'
      )}

      {/* Group */}
      {btn(() => canvasManager.groupSelected(), 'Gruppieren (Ctrl+G)',
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="2" width="8" height="8" rx="1"/><rect x="14" y="14" width="8" height="8" rx="1"/><path d="M10 6h4M6 10v4M14 18h-4M18 14v-4"/></svg>,
        !hasSelection, false, 'btn-group'
      )}
      {btn(() => canvasManager.ungroupSelected(), 'Gruppierung auflösen',
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="2" width="8" height="8" rx="1"/><rect x="14" y="14" width="8" height="8" rx="1"/><path d="M10 6h1M13 6h1M6 10v1M6 13v1"/></svg>,
        !hasSelection
      )}

      <div className="selenny-divider" />

      {/* Grid */}
      {btn(
        () => gridManager?.toggle(),
        gridVisible ? 'Raster ausblenden' : 'Raster einblenden',
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18"/><path d="M3 9h18M3 15h18M9 3v18M15 3v18"/></svg>,
        false,
        gridVisible, 'btn-grid'
      )}

      {/* Rulers */}
      {btn(
        onToggleRulers,
        showRulers ? 'Lineale ausblenden' : 'Lineale einblenden',
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="1"/><path d="M3 8h4M3 13h3M3 18h4M8 3v4M13 3v3M18 3v4"/></svg>,
        false,
        showRulers, 'btn-rulers'
      )}

      {/* Spacer to push preview to right */}
      <div className="flex-1" />

      {/* Sound Toggle + Dark Mode — right-aligned with label */}
      <div className="flex items-center" style={{ gap: '15px' }}>
        {onToggleSpeech && (
          <button
            onClick={onToggleSpeech}
            data-tooltip={speechEnabled ? 'Ton ausschalten' : 'Ton einschalten'}
            data-tooltip-pos="bottom"
            className="relative flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold transition-all duration-200"
            style={speechEnabled ? {
              background: 'linear-gradient(135deg, #00ff88, #00cc66)',
              color: '#003d1a',
              boxShadow: '0 0 12px rgba(0,255,136,0.4), 0 0 4px rgba(0,255,136,0.2)',
            } : {
              background: darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
              color: darkMode ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.35)',
            }}
          >
            {speechEnabled ? (
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><path d="M15.54 8.46a5 5 0 0 1 0 7.07"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14"/></svg>
            ) : (
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><line x1="23" y1="9" x2="17" y2="15"/><line x1="17" y1="9" x2="23" y2="15"/></svg>
            )}
            {speechEnabled ? 'Ton an' : 'Ton aus'}
          </button>
        )}

        {/* Dark Mode Toggle — labeled button so mode is clear */}
        {onToggleDark && (
          <button
            onClick={onToggleDark}
            data-tooltip={darkMode ? 'Zum hellen Modus wechseln' : 'Zum dunklen Modus wechseln'}
            data-tooltip-pos="bottom"
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold transition-all duration-200"
            style={darkMode ? {
              background: 'linear-gradient(135deg, #fbbf24, #f59e0b)',
              color: '#78350f',
              boxShadow: '0 0 10px rgba(251,191,36,0.3)',
            } : {
              background: 'linear-gradient(135deg, #1e3a5f, #0f172a)',
              color: '#93c5fd',
              boxShadow: '0 0 10px rgba(30,58,95,0.3)',
            }}
          >
            {darkMode ? (
              <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>
            ) : (
              <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z"/></svg>
            )}
            {darkMode ? 'Hell' : 'Dunkel'}
          </button>
        )}

        {/* Share / Teilen */}
        {onShare && !isSharedView && (
          <button
            onClick={onShare}
            data-tooltip="Design teilen"
            data-tooltip-pos="bottom"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all duration-150 bg-emerald-50 text-emerald-600 border border-emerald-200 hover:bg-emerald-100 hover:border-emerald-300"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>
            Teilen
          </button>
        )}

        {/* Preview */}
        <button
          onClick={onTogglePreview}
          data-tooltip={previewMode ? 'Vorschau beenden' : 'Vorschau anzeigen'}
          data-tooltip-pos="bottom"
          data-guide-id="btn-preview"
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all duration-150 ${
            previewMode
              ? 'bg-blue-600 text-white shadow-sm hover:bg-blue-700'
              : 'bg-blue-50 text-blue-600 border border-blue-200 hover:bg-blue-100 hover:border-blue-300'
          }`}
        >
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8S1 12 1 12z"/><circle cx="12" cy="12" r="3"/></svg>
          Vorschau
        </button>

        {/* Als Vorlage speichern — Admin + bestehendes Design (auch im Embedded-Mode) */}
        {isAdmin && designId && typeof onCopyAsTemplate === 'function' && (
          <button
            onClick={onCopyAsTemplate}
            data-tooltip="Dieses Design als neue Vorlage uebernehmen (Original bleibt unveraendert)"
            data-tooltip-pos="bottom"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-300 hover:bg-amber-100"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
            Als Vorlage speichern
          </button>
        )}

        {/* Zurück zum Shop — only in embedded mode */}
        {isEmbedded && (
          <button
            onClick={() => {
              if (window.parent !== window) {
                window.parent.postMessage({ type: 'designer-close' }, '*');
              }
            }}
            data-tooltip="Zurück zum Shop"
            data-tooltip-pos="bottom"
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-bold"
            style={{ background: 'linear-gradient(135deg, #00ff88, #00ccff)', color: '#000', border: 'none', cursor: 'pointer', animation: 'selenny-neon-pulse 1.5s ease-in-out infinite', letterSpacing: '0.3px' }}
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5"/><path d="m12 19-7-7 7-7"/></svg>
            Zurück zum Shop
          </button>
        )}
      </div>
    </div>
  );
}

export default TopToolbar;
